import {
  IMachineContext,
  IStateMetaData,
  TInputTypes,
  TMachineActions,
  TMachineEvents,
  TMachineStates,
  TRefEventStates,
  TRefEventsActions,
  TRegularPlayStates,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import {
  handleBackFromRefSelect,
  handleChains,
  handleChallenge,
  handlePenalty,
  handleUnderReview,
  refHandleOutcome,
} from './refUtil';
import { MAIN_MACHINE_ID } from '@/constants';

export const REGULAR_PLAY_REF_WF: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TRefEventStates.SELECT_REF_TYPE,
  states: {
    [TRefEventStates.SELECT_REF_TYPE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Ref Event',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: refHandleOutcome,
        onBack: handleBackFromRefSelect,
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK_FROM_REF]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_PLAY_DESIGN}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_UNDER_REVIEW]: {
          target: TRefEventStates.UNDER_REVIEW,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_CHALLENGE]: {
          target: TRefEventStates.CHALLANGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.PENALTY_SELECTED]: {
          target: TRefEventStates.PENALTY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_CHAINS]: {
          target: TRefEventStates.CHAINS,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_REF_DEFAULT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_YARDAGE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_IDLE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PASS_RESULT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_PASS_RESULT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_CONFIRMATION]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.CONFIRMATION}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.UNDER_REVIEW]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Under Review',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: handleUnderReview,
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_UNDER_REVIEW_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_YARDAGE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.COMPLETE_PLAY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TRefEventsActions.ON_OVERTURNED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TRefEventsActions.BACK_TO_CHALLANGE]: {
          target: TRefEventStates.CHALLANGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_IDLE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.CHAINS]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Chain Measurement',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: handleChains,
        onBack({ send }) {
          send(TRefEventsActions.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.COMPLETE_PLAY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TRefEventsActions.ON_CHAINS_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_YARDAGE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.CHALLANGE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Challenge',
          showLogo: false,
          showBackBtn: true,
        },
        onNext: handleChallenge,
        onBack({ send }) {
          send(TRefEventsActions.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_CHALLENGE_SELECTED]: {
          target: TRefEventStates.UNDER_REVIEW,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
    [TRefEventStates.PENALTY]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Penalty',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: handlePenalty,
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK]: {
          target: TRefEventStates.SELECT_REF_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PASS_RESULT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_PASS_RESULT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PLAY_DESIGN]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_PLAY_DESIGN}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_FUMBLE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_FUMBLE_RECOVERY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PENALTY_DECLINED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TRefEventsActions.ON_PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_YARDAGE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.ON_POSSESSION_SELECT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_POSSESSION}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PENALTY_DECLINED_TO_CONFIRM]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.CONFIRMATION}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
  },
};
